'use client';

import { useEffect, useMemo, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { OAuthButtons } from '@/components/OAuthButtons';
import { SiteLogoWide } from '@/components/SiteLogoWide';
import { useUserInfo } from '@/context/UserInfoContext';
import { API_URL } from '@/lib/consts';
import { OAuthIntent } from '@/lib/oauth';

enum LoginState {
  // On page load, no session established
  NotLoggedIn,

  // Sent credentials to backend, waiting for response
  // validating those credentials
  SendingCredentials,

  // Credentials validated, waiting for session to be created
  // on the backend and for auth requests to succeed
  WaitingForSession,

  // Backend is returning that the user cookie is valid
  SessionEstablished,

  // We've requested a redirect
  Redirecting,
}

/** Starting interval for the session-check polling loop after login submission. */
const INITIAL_POLL_INTERVAL_MS = 200;
/** Upper bound for the exponential backoff polling interval. */
const MAX_POLL_INTERVAL_MS = 5000;

/** Computes the next polling interval by multiplying the current interval by 1.6, capped at `MAX_POLL_INTERVAL_MS`. */
const backoffPollInterval = (currentIntervalMs: number): number => {
  return Math.min(Math.round(currentIntervalMs * 1.6), MAX_POLL_INTERVAL_MS);
};

interface LoginFormProps {
  alreadyLoggedIn: boolean;
}

// TODO(1): we are still getting failure-to-redirect sometimes.
const LoginForm: React.FC<LoginFormProps> = ({ alreadyLoggedIn }) => {
  const auth_check_url = `${API_URL}/auth/check`;

  const router = useRouter();
  const [loginState, setLoginState] = useState<LoginState>(LoginState.NotLoggedIn);
  const [loginErrorMsg, setLoginErrorMsg] = useState<string>('');
  const [pollIntervalMs, setPollIntervalMs] = useState<number>(INITIAL_POLL_INTERVAL_MS);

  const { userInfo, fetchUserInfo } = useUserInfo();

  // Announce login state changes to screen readers
  // TODO(2): Not clear if this really should be done, we don't announce the login
  // state to non-screen-reader users...
  useEffect(() => {
    const message =
      loginState === LoginState.NotLoggedIn
        ? 'Login form ready'
        : loginState === LoginState.SendingCredentials
          ? 'Sending login credentials'
          : loginState === LoginState.WaitingForSession
            ? 'Waiting for session'
            : loginState === LoginState.SessionEstablished
              ? 'Session established'
              : loginState === LoginState.Redirecting
                ? 'Redirecting to home page'
                : 'Unknown login state';

    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    Object.assign(announcer.style, visuallyHidden);
    announcer.textContent = message;
    document.body.appendChild(announcer);

    return () => {
      document.body.removeChild(announcer);
    };
  }, [loginState]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let cancelled = false;

    if (loginState === LoginState.WaitingForSession) {
      intervalId = setInterval(async () => {
        try {
          const res = await fetch(auth_check_url, { credentials: 'include' });
          if (!res.ok) {
            if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
            return;
          }

          const payload = (await res.json()) as { valid?: boolean };
          if (payload.valid) {
            const nextUserInfo = await fetchUserInfo();
            if (!cancelled && nextUserInfo) {
              setLoginState(LoginState.SessionEstablished);
            } else if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
          } else {
            if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
          }
        } catch {
          if (!cancelled) {
            setPollIntervalMs((prev) => backoffPollInterval(prev));
          }
        }
      }, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [auth_check_url, fetchUserInfo, loginState, pollIntervalMs]);

  const redirectPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return new URLSearchParams(window.location.search).get('redirect');
  }, []);

  // Handle redirect once we have user info.  We show a welcome
  // page if the user hasn't seen it yet, except that if the user requested a
  // specific redirect, we do that instead (this should be rare, and unharmful
  // since the user has a chance to see it next time they log in -- although it
  // might be a bit confusing).
  useEffect(() => {
    if (loginState === LoginState.SessionEstablished && userInfo) {
      // console.log('Have user info, redirecting', userInfo);
      setLoginState(LoginState.Redirecting);
      if (redirectPath) {
        router.push(redirectPath);
      } else {
        router.push('/');
      }
      router.refresh();
    }
  }, [loginState, redirectPath, router, userInfo]);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginState(LoginState.SendingCredentials);
    setLoginErrorMsg('');
    setPollIntervalMs(INITIAL_POLL_INTERVAL_MS);

    const formData = new FormData(event.currentTarget);

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.get('username'),
          password: formData.get('password'),
        }),
      });

      if (res.ok) {
        // Do nothing here; a useEffect will redirect us to the home page after
        // the backend is able to validate the cookie.
        setLoginState(LoginState.WaitingForSession);
      } else {
        console.log('auth request fail', res);
        const errorData = await res.json();
        setLoginErrorMsg(errorData.message || 'Login failed. Please try again.');
        setLoginState(LoginState.NotLoggedIn);
      }
    } catch (error) {
      console.log('auth request error', error);
      setLoginErrorMsg('An error occurred. Please try again.');
      setLoginState(LoginState.NotLoggedIn);
    }
  };

  if (alreadyLoggedIn) {
    return (
      <section>
        <h2>You are already logged in!</h2>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <Link href="/">Go to home page</Link>
        </Typography>
        {/* TODO(2): logout button */}
      </section>
    );
  }

  return (
    // method="POST" shouldn't be required for a javascript handler, but we've
    // seen credentials occasionally appear in the URL bar (usually when there's
    // some backend problem) so we added this to see if it helps.
    <Box component="form" method="POST" onSubmit={handleLoginSubmit} aria-label="Login form">
      <Stack spacing={3}>
        <TextField
          id="username"
          name="username"
          label="Username or email"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          inputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            spellCheck: false,
          }}
        />
        <TextField
          id="password"
          name="password"
          label="Password"
          type="password"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
        />
        <Typography variant="body2" sx={{ textAlign: 'right' }}>
          <Link href="/reset-password/request">Forgot password?</Link>
        </Typography>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loginState !== LoginState.NotLoggedIn}
          aria-disabled={loginState !== LoginState.NotLoggedIn}
        >
          Login
        </Button>
        {loginState !== LoginState.NotLoggedIn && (
          <Alert severity="info" aria-live="polite" aria-atomic="true">
            {loginState === LoginState.SendingCredentials
              ? 'Logging in...'
              : loginState === LoginState.WaitingForSession
                ? 'Redirecting...'
                : loginState === LoginState.SessionEstablished
                  ? 'You are already logged in!'
                  : loginState === LoginState.Redirecting
                    ? 'Redirecting...'
                    : 'unknown login state'}
          </Alert>
        )}
        {loginErrorMsg && (
          <Alert severity="error" role="alert">
            {loginErrorMsg}
          </Alert>
        )}
        {/* <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <Link href="/register" style={{ color: 'inherit' }}>
            Register
          </Link>
        </Typography> */}
      </Stack>
    </Box>
  );
};

const Home: React.FC = () => {
  const alreadyLoggedIn = false; // TODO(1): check if already logged in

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <SiteLogoWide heightPx={48} href={null} />
          </Box>
          <LoginForm alreadyLoggedIn={alreadyLoggedIn} />
          {!alreadyLoggedIn && <OAuthButtons intent={OAuthIntent.Login} />}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Home;
